import React, { useState, useMemo } from 'react';
import { MapPin, Clock, Calendar, ChevronLeft, ChevronRight, Camera, Ship, Tag, DollarSign } from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';
import { useSettings } from '../context/SettingsContext';

const BoatCard = ({ boat, onClick }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = boat.imageUrls || [];
  const hasImages = images.length > 0;
  
  const { showWholesalePrice, showMarginPrice, formatPriceWithMargin } = useSettings();
  
  // Process image URLs synchronously using useMemo to avoid first-render issues
  const processedImages = useMemo(() => {
    if (!hasImages) return [];
    return images.map(url => getImageUrl(url, true)); // Use proxy for all external images
  }, [images, hasImages]);

  const formatPrice = (price) => {
    if (!price) return 'Contact for Price';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatHours = (hours) => {
    if (!hours) return 'N/A';
    return `${hours} hrs`;
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleImageError = (e) => {
    // First fallback: try without CORS (some servers might block cross-origin)
    const originalSrc = e.target.dataset.originalSrc || e.target.src;
    
    // If we haven't tried a proxy yet
    if (!e.target.dataset.proxyTried) {
      e.target.dataset.proxyTried = 'true';
      e.target.dataset.originalSrc = originalSrc;
      
      // Try using a CORS proxy (you can replace with your own proxy)
      // For production, you should proxy through your own server
      console.log(`Image failed to load, trying without CORS restrictions: ${originalSrc}`);
      
      // For now, just show placeholder
      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="sans-serif" font-size="20"%3ENo Image Available%3C/text%3E%3C/svg%3E';
    }
  };

  return (
    <div className="boat-card" onClick={onClick}>
      <div className="boat-image-container">
        {hasImages ? (
          <>
            <img
              src={processedImages[currentImageIndex]}
              alt={boat.title}
              onError={handleImageError}
              loading="lazy"
            />
            {images.length > 1 && (
              <>
                <button className="image-nav prev" onClick={prevImage}>
                  <ChevronLeft size={20} />
                </button>
                <button className="image-nav next" onClick={nextImage}>
                  <ChevronRight size={20} />
                </button>
                <div className="image-indicator">
                  <Camera size={14} />
                  {currentImageIndex + 1} / {images.length}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="no-image">
            <img 
              src="/centurion-logo.svg"
              alt="Centurion Boats"
              className="no-image-logo"
            />
            <p>No Image Available</p>
          </div>
        )}
        <div className="card-watermark">
          <img 
            src="/centurion-logo.svg"
            alt="Centurion"
            className="watermark-logo"
          />
        </div>
      </div>
      
      <div className="boat-details">
        <h3>{boat.title || `${boat.year} ${boat.model}`}</h3>
        <div className="boat-price">{formatPrice(boat.price)}</div>
        
        {showWholesalePrice && boat.wholesalePrice && (
          <div className="wholesale-price">
            <Tag size={12} />
            <span>Wholesale: {formatPrice(boat.wholesalePrice)}</span>
          </div>
        )}
        
        {showMarginPrice && boat.wholesalePrice && (
          <div className="margin-price">
            <DollarSign size={12} />
            <span>Sale Price: {formatPriceWithMargin(boat.wholesalePrice)}</span>
          </div>
        )}
        
        <div className="boat-info">
          <div className="info-item">
            <Calendar size={16} />
            <span>{boat.year} {boat.model}</span>
          </div>
          <div className="info-item">
            <Clock size={16} />
            <span>{formatHours(boat.hours)}</span>
          </div>
          <div className="info-item">
            <MapPin size={16} />
            <span>{boat.locationCity}, {boat.locationState}</span>
          </div>
        </div>

        {boat.stocknumber && (
          <div className="stock-number">Stock #: {boat.stocknumber}</div>
        )}
      </div>
    </div>
  );
};

export default BoatCard;
